import { useEffect, useRef, useState } from 'react';
import { TbX } from 'react-icons/tb';
import { ExerciseGif } from './ExerciseGif';
import './training.css';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 24;

// Real `body_part` values from api/src/data/exercises.json (verified with a
// node one-liner over the dataset): back, cardio, chest, lower arms, lower
// legs, neck, shoulders, upper arms, upper legs, waist. `GET /exercises`
// filters on this field via the `bodyPart` query param (see
// api/src/gym/exercise-catalog-service.js) — NOT `target`, which holds a
// specific muscle (pectorals, lats, delts, quads...) that no fixed dropdown
// could enumerate sensibly. Fix round 1: this filter used to send these
// labels' values as `target`, so every filtered search matched zero rows.
const BODY_PART_OPTIONS = [
  { value: '', label: 'Todo el cuerpo' },
  { value: 'chest', label: 'Pecho' },
  { value: 'back', label: 'Espalda' },
  { value: 'shoulders', label: 'Hombros' },
  { value: 'upper arms', label: 'Brazos' },
  { value: 'lower arms', label: 'Antebrazos' },
  { value: 'upper legs', label: 'Piernas' },
  { value: 'lower legs', label: 'Pantorrillas' },
  { value: 'waist', label: 'Cintura/Abdomen' },
  { value: 'neck', label: 'Cuello' },
  { value: 'cardio', label: 'Cardio' },
];

/**
 * Exercise picker (spec "Cambiar ejercicio"): `mode="search"` is a debounced
 * (300ms) name search with an optional body-part filter (`bodyPart`, the
 * real `GET /exercises` field — not `target`, a specific muscle) and
 * "Cargar más" pagination (fetched immediately, not debounced);
 * `mode="alternatives"` loads `api.listAlternatives(baseExerciseId)`
 * once, immediately, with no search UI (the server already scoped the list to
 * the same target muscle). Search defaults to multiple selection for adding
 * exercises; callers replacing one existing item can set `selectionMode="single"`.
 * Alternatives remain single-choice and call `onSelect(exercise)`. A monotonic request id guards
 * against an in-flight response from a superseded query overwriting newer
 * results (a slow first keystroke resolving after a faster later one).
 * Optional `onModeChange(mode)` (Task 8, "Mi rutina"): when given, the header
 * shows "Alternativas" / "Buscar" tabs so the member can leave the
 * same-muscle list and search the whole catalog; the parent owns `mode`.
 */
export function ExercisePicker({
  api, mode = 'search', selectionMode = 'multiple', baseExerciseId, onSelect, onConfirmSelection, onClose, onModeChange,
}) {
  const [query, setQuery] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [withMedia, setWithMedia] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedById, setSelectedById] = useState(() => new Map());
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (mode !== 'alternatives') return undefined;
    const requestId = ++requestIdRef.current;
    setStatus('loading');
    api.listAlternatives(baseExerciseId)
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        setExercises(Array.isArray(result) ? result : []);
        setTotal(Array.isArray(result) ? result.length : 0);
        setStatus('ready');
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setStatus('error');
      });
    return undefined;
  }, [mode, baseExerciseId, api]);

  // Debounced (300ms) first-page search, re-armed on every query/bodyPart/media
  // change. This is the ONLY thing the 300ms wait applies to; pagination
  // (`handleLoadMore` below) is a direct user action on already-visible
  // results and must not be held up by it (fix round 1, item 3).
  useEffect(() => {
    if (mode !== 'search') return undefined;
    setStatus('loading');
    const timer = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      api.listExercises({ q: query || undefined, bodyPart: bodyPart || undefined, ...(withMedia ? { withMedia: true } : {}), limit: PAGE_SIZE, offset: 0 })
        .then((result) => {
          if (requestIdRef.current !== requestId) return;
          setExercises((result && result.exercises) || []);
          setTotal((result && result.total) || 0);
          setStatus('ready');
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) return;
          setStatus('error');
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, query, bodyPart, withMedia, api]);

  function handleLoadMore() {
    const requestId = ++requestIdRef.current;
    setLoadingMore(true);
    api.listExercises({ q: query || undefined, bodyPart: bodyPart || undefined, ...(withMedia ? { withMedia: true } : {}), limit: PAGE_SIZE, offset: exercises.length })
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        const list = (result && result.exercises) || [];
        setExercises((current) => [...current, ...list]);
        setTotal((result && result.total) || 0);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setStatus('error');
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setLoadingMore(false);
      });
  }

  const isAlternatives = mode === 'alternatives';
  const isSingleChoice = isAlternatives || selectionMode === 'single';
  const canLoadMore = !isAlternatives && status === 'ready' && exercises.length < total;
  const selectedExercises = [...selectedById.values()];

  function toggleExercise(exercise) {
    setSelectedById((current) => {
      const next = new Map(current);
      if (next.has(exercise.id)) next.delete(exercise.id);
      else next.set(exercise.id, exercise);
      return next;
    });
  }

  function confirmSelection() {
    if (selectedExercises.length === 0 || typeof onConfirmSelection !== 'function') return;
    onConfirmSelection(selectedExercises);
    onClose();
  }

  return (
    <div className="gym-ex-picker-backdrop" onMouseDown={onClose}>
      <div
        className="gym-ex-picker"
        role="dialog"
        aria-modal="true"
        aria-label={isAlternatives ? 'Elegir ejercicio alternativo' : 'Buscar ejercicio'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="gym-ex-picker__header">
          <h2 className="gym-ex-picker__title">{isAlternatives ? 'Cambiar ejercicio' : 'Buscar ejercicio'}</h2>
          <button type="button" className="gym-ex-picker__close" onClick={onClose} aria-label="Cerrar">
            <TbX aria-hidden="true" focusable="false" />
          </button>
        </div>
        {typeof onModeChange === 'function' && (
          <div className="gym-ex-picker__tabs" role="tablist" aria-label="Cómo elegir">
            {[['alternatives', 'Alternativas'], ['search', 'Buscar']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                className={`gym-ex-picker__tab${mode === value ? ' is-active' : ''}`}
                onClick={() => { if (mode !== value) onModeChange(value); }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {!isAlternatives && (
          <div className="gym-ex-picker__controls">
            <label className="gym-sr-only" htmlFor="gym-ex-picker-query">Buscar ejercicio por nombre</label>
            <input
              id="gym-ex-picker-query"
              type="search"
              className="gym-input"
              placeholder="Buscá en español o inglés (ej.: press de banca)"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <label className="gym-sr-only" htmlFor="gym-ex-picker-bodypart">Filtrar por zona del cuerpo</label>
            <select
              id="gym-ex-picker-bodypart"
              className="gym-input"
              value={bodyPart}
              onChange={(event) => setBodyPart(event.target.value)}
            >
              {BODY_PART_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <label className="gym-ex-picker__media-filter">
              <input type="checkbox" checked={withMedia} onChange={(event) => setWithMedia(event.target.checked)} />
              Mostrar solo ejercicios con imagen
            </label>
          </div>
        )}
        {status === 'error' && <p className="gym-ex-picker__error" role="alert">No se pudieron cargar los ejercicios.</p>}
        {status === 'loading' && exercises.length === 0 && (
          <p className="gym-ex-picker__status" role="status">Buscando…</p>
        )}
        {status === 'ready' && exercises.length === 0 && (
          <p className="gym-ex-picker__status">{mode === 'search' && withMedia
            ? 'No hay ejercicios con imagen para esa búsqueda. Destildá «Mostrar solo ejercicios con imagen» para ver todos.'
            : 'No se encontraron ejercicios.'}</p>
        )}
        <div className="gym-ex-picker__grid">
          {exercises.map((exercise) => (
            <button
              key={exercise.id}
              type="button"
              className={`gym-ex-picker__item${!isSingleChoice && selectedById.has(exercise.id) ? ' is-selected' : ''}`}
              aria-pressed={isSingleChoice ? undefined : selectedById.has(exercise.id)}
              onClick={() => (isSingleChoice ? onSelect(exercise) : toggleExercise(exercise))}
            >
              <ExerciseGif exercise={exercise} size="sm" loading="eager" />
              <span className="gym-ex-picker__item-text">
                <span className="gym-ex-picker__item-name">{exercise.name}</span>
                {exercise.nameEn && exercise.nameEn !== exercise.name && (
                  <span className="gym-ex-picker__item-name-en">{exercise.nameEn}</span>
                )}
                {exercise.isCustom && <span className="gym-ex-picker__own">Propio</span>}
              </span>
              {!isSingleChoice && selectedById.has(exercise.id) && <span className="gym-ex-picker__selected-mark" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
        {canLoadMore && (
          <button
            type="button"
            className="gym-button gym-button--secondary gym-ex-picker__more"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Cargando…' : 'Cargar más'}
          </button>
        )}
        {!isSingleChoice && (
          <div className="gym-ex-picker__selection-footer">
            <span aria-live="polite">{selectedExercises.length} {selectedExercises.length === 1 ? 'seleccionado' : 'seleccionados'}</span>
            <button
              type="button"
              className="gym-button gym-button--primary"
              disabled={selectedExercises.length === 0}
              onClick={confirmSelection}
            >
              Agregar seleccionados
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
